/**
 * Flybyu case-pricing live preview widget.
 *
 * Binds to the real product-page quantity input (by id) and, as the
 * shopper changes quantity, shows what the live "Case Pricing" automatic
 * discounts (assets/flybyu-case-pricing.js) will actually apply in cart —
 * so there's no app, and no separate/duplicate quantity control to keep
 * in sync with the one that actually gets submitted to cart.
 *
 * Caveat: the unit price used here is captured at page load
 * (data-fby-case-pricing-unit-price). If a shopper switches product
 * variant via an AJAX variant picker (no full page reload) to a variant
 * with a different price, this preview will not pick up the new price
 * until the page is reloaded. In practice the eligible oil variants for a
 * given product share one price, so this is expected to be rare.
 *
 * 2026-09-19 (Derek): the 3+/6+/12+ tier pills are now clickable — clicking
 * one sets the real quantity input to that tier's qty (same as typing it
 * in yourself), so a shopper can jump straight to "buy a case of 12" in
 * one click instead of using the +/- stepper. This does NOT add to cart by
 * itself — it only fills in the quantity; the shopper still presses the
 * page's own Add to Cart button, same as always.
 *
 * 2026-09-19, same day (Derek): tier pills now read "3-pack"/"6-pack"/
 * "12-pack" (text lives in the liquid snippet, not here) instead of
 * showing their own "X% off" — that percent-off now shows only in the
 * result box below, and this file tags the badge with the active tier's
 * qty (data-fby-tier-color) so assets/flybyu-case-pricing.css can color
 * it to match that tier's pill (red/yellow/green) instead of always
 * being the same dark/yellow.
 *
 * 2026-09-19, same day (Derek): selecting a tier (or typing/stepping to a
 * quantity that reaches one) now also updates the *real* product price
 * display and the "Pay in N interest-free installments... with Shop"
 * messaging below it, so the page reflects the pack's actual total
 * instead of staying at the flat unit price. This intentionally does not
 * try to recompute Shop's installment math itself — it only rewrites the
 * shopify-payment-terms element's shopify-meta attribute with the new
 * full_price and lets that component's own logic recalculate everything
 * (installment count, bracket messaging, etc.), which is what it already
 * does correctly on its own.
 *
 * 2026-10-28 (Derek): fixed two real bugs in the installments update above,
 * both confirmed live in the browser against the real shopify-payment-terms
 * component (no custom installment math added either time):
 *  1. It was only ever rewriting full_price (the formatted display string,
 *     e.g. "$221.13"), never full_price_amount (the numeric value the
 *     component's own bracket/installment-count math actually reads). The
 *     installment message silently kept computing off the original 1-unit
 *     amount no matter what pack was selected, even though the main price
 *     and the formatted full_price string both updated correctly. Fixed by
 *     setting full_price_amount alongside full_price on every update.
 *  2. For any product whose single-unit price is under Shop's $35
 *     installments minimum (common across this catalog's lower-priced
 *     oils), Shopify's own server-rendered shopify-meta sets that variant's
 *     eligible flag to false at page load, and the component shows its
 *     generic "Pay over time for orders over $35.00" message instead of
 *     real terms — and keeps showing it even once a 3/6/12-pack pushes the
 *     total well above $35, because this widget never touched the eligible
 *     flag, only full_price/full_price_amount. Fixed by recomputing
 *     eligible from the pristine meta's own min_price_amount/max_price_amount
 *     against the new total on every update, so a pack that crosses the
 *     threshold correctly switches to the real installment-count message.
 *
 * 2026-10-28, later same day (Derek): found and fixed a third bug Derek
 * caught by hand — on a "Pay in N interest-free installments of $X" total
 * (the simple message Shop shows when a price falls in a bracket that only
 * has 0%-APR plans, e.g. $50–$149.99 here), N and $X stopped agreeing after
 * a tier was selected: e.g. 3-pack = $116.71 showed "Pay in 2 installments
 * of $29.18" — but 2 × $29.18 = $58.36, not $116.71. Confirmed live that
 * shopify-payment-terms recomputes the dollar figure correctly from the
 * fresh full_price_amount (dividing by the RIGHT installment count for the
 * new bracket, 4 in this case) but still prints the installment-count word
 * straight from the untouched, page-load number_of_payment_terms field (2,
 * correct only for the original 1-unit price's own bracket) — the two
 * numbers come from different math. This only affects brackets with
 * exclusively 0%-APR plans; the "N interest-free installments, or from
 * $X/mo" message Shop uses for pricier brackets (6-pack/12-pack here)
 * already recomputes its own headline count correctly and needed no fix.
 * Fixed by working out, from the pristine meta's own financing_plans table,
 * which price bracket the new total falls into and the highest 0%-APR
 * installment count in it (mirroring what the component itself already
 * does for the dollar figure), then writing that count into
 * number_of_payment_terms (and price_per_term/price_per_term_amount, kept
 * in sync for consistency) alongside full_price_amount. Still no custom
 * installment math: this only tells the component which bracket applies,
 * using numbers taken directly from its own shopify-meta data.
 */
(function () {
  'use strict';

  if (!window.FlybyuCasePricing) return;

  var MONEY_FORMAT =
    (window.themeCore &&
      window.themeCore.objects &&
      window.themeCore.objects.shop &&
      window.themeCore.objects.shop.money_format) ||
    '${{amount}}';

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents) || 0;

    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    format = format || MONEY_FORMAT;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number)) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var match = format.match(placeholderRegex);
    if (!match) return format;

    switch (match[1]) {
      case 'amount':
        value = withDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = withDelimiters(cents, 2);
    }

    return format.replace(placeholderRegex, value);
  }

  function initWidget(root) {
    if (root.dataset.fbyInit === 'true') return;
    root.dataset.fbyInit = 'true';

    var variantId = root.getAttribute('data-fby-case-pricing-variant-id');
    var unitPrice = parseInt(root.getAttribute('data-fby-case-pricing-unit-price'), 10);
    var qtyInputId = root.getAttribute('data-fby-case-pricing-quantity-input-id');
    var qtyInput = qtyInputId ? document.getElementById(qtyInputId) : null;

    if (!qtyInput || isNaN(unitPrice) || !window.FlybyuCasePricing.isEligible(variantId)) {
      root.hidden = true;
      return;
    }

    var tierEls = root.querySelectorAll('[data-fby-tier-qty]');
    var resultEl = root.querySelector('[data-fby-case-pricing-result]');
    var badgeEl = root.querySelector('[data-fby-case-pricing-badge]');
    var originalEl = root.querySelector('[data-fby-case-pricing-original]');
    var discountedEl = root.querySelector('[data-fby-case-pricing-discounted]');
    var savingsEl = root.querySelector('[data-fby-case-pricing-savings]');
    var nudgeEl = root.querySelector('[data-fby-case-pricing-nudge]');

    // Scope the main price / installments lookups to this product's own
    // form so a case-pricing widget never touches an unrelated price
    // display elsewhere on the page (e.g. a recommendations carousel).
    var formScope = qtyInput.closest('.shopify-product-form') || root.closest('.shopify-product-form') || document;
    var priceRegularEl = formScope.querySelector('.price-item--regular[data-product-price]');
    var priceSaleEl = formScope.querySelector('.price-item--sale[data-product-price]');
    var paymentTermsEl = formScope.querySelector('shopify-payment-terms');

    // The main price display's text includes a trailing currency-code
    // suffix (e.g. "$40.95 CAD") that formatMoney()'s output doesn't
    // produce on its own — capture it once at load so updates keep it,
    // instead of hardcoding "CAD".
    var priceCurrencySuffix = '';
    if (priceRegularEl) {
      var suffixMatch = priceRegularEl.textContent.trim().match(/[A-Za-z]{2,5}$/);
      if (suffixMatch) priceCurrencySuffix = ' ' + suffixMatch[0];
    }

    // shopify-payment-terms recalculates its own installment messaging
    // (count, brackets, "or from $X/mo", below-minimum text, etc.)
    // whenever its shopify-meta attribute is re-set — but it reads the
    // NUMERIC full_price_amount field to pick which bracket/installment
    // count applies, not the formatted full_price string. Both fields
    // must be kept in sync, or the message silently keeps showing the
    // original quantity's bracket (confirmed live in the browser — see
    // the 2026-10-28 note at the top of this file). Cache the pristine
    // attribute once so every later update starts from the real original
    // data (financing_plans, min_price, max_price, etc.) and only
    // overwrites full_price / full_price_amount.
    var pristineShopifyMeta = null;
    if (paymentTermsEl) {
      var rawMeta = paymentTermsEl.getAttribute('shopify-meta');
      if (rawMeta) {
        try {
          pristineShopifyMeta = JSON.parse(rawMeta);
        } catch (e) {
          pristineShopifyMeta = null;
        }
      }
    }

    // Works out which of the shop's own financing_plans brackets a total
    // falls into, and the highest 0%-APR (split_pay) installment count
    // offered in that bracket — the same count shopify-payment-terms
    // itself already uses when it recomputes the per-term dollar amount
    // from full_price_amount. Returns null if no bracket matches, or the
    // matching bracket has no interest-free option at all (e.g. $1000+),
    // in which case number_of_payment_terms is left untouched rather than
    // guessed at.
    function pickInterestFreeTermCount(totalAmount, financingPlans) {
      if (!Array.isArray(financingPlans)) return null;

      for (var i = 0; i < financingPlans.length; i++) {
        var plan = financingPlans[i];
        if (
          typeof plan.min_price_amount !== 'number' ||
          typeof plan.max_price_amount !== 'number' ||
          totalAmount < plan.min_price_amount ||
          totalAmount > plan.max_price_amount
        ) {
          continue;
        }

        var terms = plan.terms || [];
        var bestCount = null;
        for (var j = 0; j < terms.length; j++) {
          if (terms[j].apr === 0 && (bestCount === null || terms[j].installments_count > bestCount)) {
            bestCount = terms[j].installments_count;
          }
        }
        return bestCount;
      }

      return null;
    }

    function updatePriceDisplay(totalCents) {
      var formatted = formatMoney(totalCents) + priceCurrencySuffix;

      if (priceRegularEl) priceRegularEl.textContent = formatted;
      if (priceSaleEl) priceSaleEl.textContent = formatted;

      if (paymentTermsEl && pristineShopifyMeta) {
        var meta = JSON.parse(JSON.stringify(pristineShopifyMeta));
        if (meta.variants && meta.variants[0]) {
          var totalAmount = totalCents / 100;

          // full_price is the formatted display string; full_price_amount
          // is the numeric value shopify-payment-terms' own bracket math
          // actually reads. Both must be set together, or the component
          // keeps computing installments off the stale numeric amount.
          meta.variants[0].full_price = formatMoney(totalCents);
          meta.variants[0].full_price_amount = totalAmount;

          // eligible is a separate static flag Shopify computes server-side
          // at page load from the ORIGINAL 1-unit price against Shop's own
          // min/max installment thresholds. A product priced under the
          // $35 minimum loads with eligible: false and the component shows
          // its generic "Pay over time for orders over $X" message — which
          // keeps showing even after a pack pushes the total above $35,
          // unless eligible is recomputed against the new total too.
          if (
            typeof meta.min_price_amount === 'number' &&
            typeof meta.max_price_amount === 'number'
          ) {
            meta.variants[0].eligible =
              totalAmount >= meta.min_price_amount && totalAmount <= meta.max_price_amount;
          }

          // number_of_payment_terms / price_per_term(_amount) back the
          // "Pay in N interest-free installments of $X" wording Shop shows
          // for brackets that only have 0%-APR plans (confirmed live: the
          // component recomputes $X fresh from full_price_amount using the
          // correct bracket's count, but prints N straight from this
          // untouched field — so the two can disagree unless this is kept
          // in sync too). Brackets with interest-bearing options already
          // render their own headline count correctly without this, so it's
          // only set when a clean 0%-APR count is found.
          var termCount = pickInterestFreeTermCount(totalAmount, pristineShopifyMeta.financing_plans);
          if (termCount) {
            var perTerm = totalAmount / termCount;
            meta.variants[0].number_of_payment_terms = termCount;
            meta.variants[0].price_per_term_amount = perTerm;
            meta.variants[0].price_per_term = formatMoney(Math.round(perTerm * 100));
          }

          paymentTermsEl.setAttribute('shopify-meta', JSON.stringify(meta));
        }
      }
    }

    function render() {
      var qty = parseInt(qtyInput.value, 10);
      if (isNaN(qty) || qty < 1) qty = 1;

      var tier = window.FlybyuCasePricing.getTier(qty);
      var next = window.FlybyuCasePricing.nextTier(qty);
      var originalTotal = unitPrice * qty;

      for (var i = 0; i < tierEls.length; i++) {
        var tierQty = parseInt(tierEls[i].getAttribute('data-fby-tier-qty'), 10);
        tierEls[i].classList.toggle('is-active', !!tier && tier.qty === tierQty);
      }

      if (tier) {
        var discountedTotal = Math.round(originalTotal * (1 - tier.pct));
        var savings = originalTotal - discountedTotal;
        var pctLabel = Math.round(tier.pct * 100) + '% off';

        badgeEl.textContent = pctLabel;
        badgeEl.setAttribute('data-fby-tier-color', tier.qty);
        originalEl.textContent = formatMoney(originalTotal);
        discountedEl.textContent = formatMoney(discountedTotal);
        savingsEl.textContent = 'You save ' + formatMoney(savings);
        resultEl.hidden = false;

        updatePriceDisplay(discountedTotal);

        if (next) {
          nudgeEl.textContent =
            'Add ' + (next.qty - qty) + ' more to save ' + Math.round(next.pct * 100) + '%';
          nudgeEl.hidden = false;
        } else {
          nudgeEl.hidden = true;
        }
      } else {
        badgeEl.removeAttribute('data-fby-tier-color');
        resultEl.hidden = true;

        updatePriceDisplay(originalTotal);

        if (next) {
          nudgeEl.textContent =
            'Add ' + (next.qty - qty) + ' more to save ' + Math.round(next.pct * 100) + '%';
          nudgeEl.hidden = false;
        } else {
          nudgeEl.hidden = true;
        }
      }
    }

    // Clicking a tier pill (3-pack/6-pack/12-pack) jumps the real quantity
    // input straight to that tier's qty, respecting the input's own
    // min/max/step if the theme set any, then fires the same input/change
    // events a shopper typing the value would — so this preview widget,
    // the theme's own quantity display, and cart all agree.
    function selectTier(tierQty) {
      var min = parseInt(qtyInput.getAttribute('min'), 10);
      var max = parseInt(qtyInput.getAttribute('max'), 10);
      var target = tierQty;
      if (!isNaN(min) && target < min) target = min;
      if (!isNaN(max) && target > max) target = max;

      qtyInput.value = target;
      qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      render();
    }

    for (var t = 0; t < tierEls.length; t++) {
      (function (tierEl) {
        var tierQty = parseInt(tierEl.getAttribute('data-fby-tier-qty'), 10);
        if (isNaN(tierQty)) return;
        tierEl.setAttribute('role', 'button');
        tierEl.setAttribute('tabindex', '0');
        tierEl.setAttribute('aria-label', 'Set quantity to ' + tierQty);
        tierEl.addEventListener('click', function () {
          selectTier(tierQty);
        });
        tierEl.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            selectTier(tierQty);
          }
        });
      })(tierEls[t]);
    }

    qtyInput.addEventListener('input', render);
    qtyInput.addEventListener('change', render);

    // Adastra's quantity +/- buttons dispatch a native "change"/"input" event
    // on the input itself in most builds, but re-render on a short delay too
    // in case a button click updates the value without firing either.
    var wrapper = qtyInput.closest('.product-quantity') || qtyInput.parentElement;
    if (wrapper) {
      wrapper.addEventListener('click', function () {
        window.setTimeout(render, 0);
      });
    }

    render();
  }

  function init() {
    var roots = document.querySelectorAll('[data-fby-case-pricing]');
    for (var i = 0; i < roots.length; i++) initWidget(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
